import Constants from 'common/constants'
import AuthService from 'common/services/auth'
import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useMemo
} from 'react'
import { setRedirectRoute } from 'router/redirect'
import AuthModule from '.'
import {
  AuthProviderInterface,
  AuthUserContextInterface,
  AuthUserInterface
} from './interfaces'

const AuthContext = createContext<AuthUserContextInterface>({
  setAuth: () => {},
  clearAuth: () => {},
  auth: null
})

function AuthProvider(props: AuthProviderInterface) {
  const [state, setState] = useState(props.auth)

  const clearAuth = useCallback(() => setState(null), [])
  const setAuth = useCallback(
    (auth: AuthUserInterface) => {
      // Só atualiza se o valor for diferente
      if (JSON.stringify(auth) !== JSON.stringify(state)) {
        setState(auth)
      }
    },
    [state]
  )

  // Memoiza os valores do contexto para evitar re-renderizações desnecessárias
  const contextValue = useMemo(
    () => ({
      auth: state,
      clearAuth,
      setAuth
    }),
    [state, clearAuth, setAuth]
  )

  return (
    <AuthContext.Provider value={contextValue}>
      {props.children}
    </AuthContext.Provider>
  )
}

function useAuth() {
  const context = useContext(AuthContext)

  const logout = useCallback(() => {
    context.clearAuth()
    localStorage.removeItem(Constants.AUTH_LOCAL_STORAGE)
    localStorage.removeItem(Constants.TOKEN_LOCAL_STORAGE)
    setRedirectRoute()
  }, [context])

  const isAuthenticated = useCallback(() => {
    if (!context.auth) {
      return false
    }

    return AuthModule.isTokenValid(context.auth.jwt)
  }, [context])

  const checkAndRedirectIfInvalid = useCallback(() => {
    if (context.auth && !AuthModule.isTokenValid(context.auth.jwt)) {
      setRedirectRoute()
    }
  }, [context])

  const loginWithToken = async (accessToken: string, refreshToken: string) => {
    localStorage.setItem(
      Constants.AUTH_LOCAL_STORAGE,
      JSON.stringify({ accessToken, refreshToken })
    )
    localStorage.setItem(Constants.TOKEN_LOCAL_STORAGE, accessToken)
    const jwt = AuthModule.parseJwt(accessToken)
    const permissions = jwt?.resource_access?.auth?.roles ?? []
    if (jwt) {
      context.setAuth({
        jwt,
        authData: {
          accessToken,
          refreshToken
        },
        permissions
      })
    }
  }

  const login = async (email: string, password: string) => {
    try {
      const { accessToken, refreshToken } = await AuthService.login(
        email,
        password
      )

      loginWithToken(accessToken, refreshToken)
    } catch (error) {
      // Re-throw the error to be handled by the login form
      throw error
    }
  }

  return {
    isAdmin: AuthModule.isAdmin(context.auth?.jwt),
    isEmployee:
      AuthModule.isEmployee(context.auth?.jwt) ||
      AuthModule.isAdmin(context.auth?.jwt),
    isCustomer: AuthModule.isCustomer(context.auth?.jwt),
    isPartnerBroker: AuthModule.isPartnerBroker(context.auth?.jwt),
    isCaptador: AuthModule.isCaptador(context.auth?.jwt),
    isAuthenticated,
    checkAndRedirectIfInvalid,
    auth: context.auth,
    logout,
    login,
    loginWithToken
  }
}

export { AuthContext, useAuth }
export default AuthProvider
